import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  LayoutDashboard,
  FileText,
  Users,
  LogOut,
  Search,
  AlertTriangle,
  MessageSquare,
  Clock,
  TrendingUp,
  ChevronRight,
  Menu,
  X,
  Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Color palettes
const CATEGORIA_COLORS = {
  fraude: "#ef4444",
  assedio: "#f97316",
  seguranca: "#eab308",
  corrupcao: "#a855f7",
  outro: "#64748b",
};

const STATUS_COLORS = {
  recebida: "#3b82f6",
  em_analise: "#eab308",
  investigacao: "#a855f7",
  concluida: "#22c55e",
  arquivada: "#64748b",
};

const OUVIDORIA_TIPO_COLORS = {
  sugestao: "#22c55e",
  reclamacao: "#ef4444",
  elogio: "#3b82f6",
  informacao: "#a855f7",
};

const OUVIDORIA_STATUS_COLORS = {
  recebida: "#3b82f6",
  em_analise: "#eab308",
  respondida: "#22c55e",
  fechada: "#64748b",
};

// Custom Tooltip component for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-[#1a1a2e]">{label || payload[0].name}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Date filters
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchData();
  }, [dateFilter, startDate, endDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params for date filter
      let params = {};
      if (dateFilter === "custom" && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (dateFilter !== "all") {
        params.period = dateFilter;
      }

      const [statsRes, timelineRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, { params }),
        axios.get(`${API_URL}/dashboard/timeline`, { params }),
      ]);
      
      setStats(statsRes.data);
      setTimeline(timelineRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/admin/casos?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  const categoriaLabels = {
    fraude: "Fraude",
    assedio: "Assédio",
    seguranca: "Segurança",
    corrupcao: "Corrupção",
    outro: "Outro",
  };

  const statusLabels = {
    recebida: "Recebida",
    em_analise: "Em Análise",
    investigacao: "Investigação",
    concluida: "Concluída",
    arquivada: "Arquivada",
  };

  const ouvidoriaTipoLabels = {
    sugestao: "Sugestão",
    reclamacao: "Reclamação",
    elogio: "Elogio",
    informacao: "Informação",
  };

  const ouvidoriaStatusLabels = {
    recebida: "Recebida",
    em_analise: "Em Análise",
    respondida: "Respondida",
    fechada: "Fechada",
  };

  // Transform data for charts
  const categoriaData = Object.entries(stats?.por_categoria || {}).map(([key, value]) => ({
    name: categoriaLabels[key] || key,
    value,
    color: CATEGORIA_COLORS[key] || "#64748b",
  }));

  const statusData = Object.entries(stats?.por_status || {}).map(([key, value]) => ({
    name: statusLabels[key] || key,
    value,
    color: STATUS_COLORS[key] || "#64748b",
  }));

  const ouvidoriaTipoData = Object.entries(stats?.ouvidoria_por_tipo || {}).map(([key, value]) => ({
    name: ouvidoriaTipoLabels[key] || key,
    value,
    color: OUVIDORIA_TIPO_COLORS[key] || "#64748b",
  }));

  const ouvidoriaStatusData = Object.entries(stats?.ouvidoria_por_status || {}).map(([key, value]) => ({
    name: ouvidoriaStatusLabels[key] || key,
    value,
    color: OUVIDORIA_STATUS_COLORS[key] || "#64748b",
  }));

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-xs font-medium">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-semibold text-[#1a1a2e]">CONTERP</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </div>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link
              to="/admin"
              className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium"
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
            <Link
              to="/admin/casos"
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
              data-testid="nav-casos"
            >
              <FileText className="w-5 h-5" />
              Casos
            </Link>
            {user?.role === "admin" && (
              <Link
                to="/admin/usuarios"
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                data-testid="nav-usuarios"
              >
                <Users className="w-5 h-5" />
                Usuários
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                {user?.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full justify-start" onClick={logout} data-testid="btn-logout">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <form onSubmit={handleSearch} className="hidden md:block">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por protocolo ou descrição..."
                    className="pl-10 w-80"
                    data-testid="search-input"
                  />
                </div>
              </form>
            </div>

            <div className="flex items-center gap-3">
            </div>
          </div>
        </header>

        <div className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
            </div>
          ) : (
            <>
              {/* Header with Date Filters */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#1a1a2e] mb-1">
                    Olá, {user?.nome?.split(" ")[0]}!
                  </h1>
                  <p className="text-slate-600">Visão geral do sistema de compliance</p>
                </div>

                {/* Date Filter */}
                <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-500" />
                    <Label className="text-sm font-medium text-slate-600">Período:</Label>
                  </div>
                  <Select value={dateFilter} onValueChange={handleDateFilterChange}>
                    <SelectTrigger className="w-[140px]" data-testid="date-filter-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="7d">Últimos 7 dias</SelectItem>
                      <SelectItem value="30d">Últimos 30 dias</SelectItem>
                      <SelectItem value="90d">Últimos 90 dias</SelectItem>
                      <SelectItem value="year">Este ano</SelectItem>
                      <SelectItem value="custom">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>

                  {dateFilter === "custom" && (
                    <div className="flex items-center gap-2">
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-[140px]"
                        data-testid="start-date"
                      />
                      <span className="text-slate-400">até</span>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-[140px]"
                        data-testid="end-date"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#1a1a2e]/10 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-[#1a1a2e]" />
                    </div>
                    <TrendingUp className="w-4 h-4 text-green-500" />
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.total_denuncias || 0}</p>
                  <p className="text-sm text-slate-500">Total Denúncias</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#f5a623]/10 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-[#f5a623]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.total_ouvidoria || 0}</p>
                  <p className="text-sm text-slate-500">Total Ouvidoria</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.casos_abertos || 0}</p>
                  <p className="text-sm text-slate-500">Casos Abertos</p>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.casos_vencidos || 0}</p>
                  <p className="text-sm text-slate-500">SLA Vencido</p>
                </div>
              </div>

              {/* Timeline Chart */}
              {timeline.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Evolução de Casos</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="denuncias"
                        name="Denúncias"
                        stroke="#1a1a2e"
                        strokeWidth={2}
                        dot={{ fill: "#1a1a2e", r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="ouvidoria"
                        name="Ouvidoria"
                        stroke="#f5a623"
                        strokeWidth={2}
                        dot={{ fill: "#f5a623", r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Denúncias by Category - Pie Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Denúncias por Categoria</h3>
                  {categoriaData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={categoriaData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {categoriaData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">
                      Nenhuma denúncia no período
                    </div>
                  )}
                </div>

                {/* Denúncias by Status - Bar Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Denúncias por Status</h3>
                  {statusData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={statusData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={100} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" name="Quantidade" radius={[0, 4, 4, 0]}>
                          {statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">
                      Nenhuma denúncia no período
                    </div>
                  )}
                </div>

                {/* Ouvidoria by Tipo - Pie Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Ouvidoria por Tipo</h3>
                  {ouvidoriaTipoData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={ouvidoriaTipoData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={renderCustomizedLabel}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {ouvidoriaTipoData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">
                      Nenhuma ouvidoria no período
                    </div>
                  )}
                </div>

                {/* Ouvidoria by Status - Bar Chart */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">Ouvidoria por Status</h3>
                  {ouvidoriaStatusData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={ouvidoriaStatusData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={100} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" name="Quantidade" radius={[0, 4, 4, 0]}>
                          {ouvidoriaStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">
                      Nenhuma ouvidoria no período
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-[#1a1a2e] mb-4">Ações Rápidas</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link
                    to="/admin/casos?tipo=denuncia&status=recebida"
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                    data-testid="quick-denuncias"
                  >
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-[#1a1a2e]" />
                      <span className="font-medium text-[#1a1a2e]">Novas Denúncias</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </Link>

                  <Link
                    to="/admin/casos?tipo=ouvidoria&status=recebida"
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                    data-testid="quick-ouvidoria"
                  >
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-[#f5a623]" />
                      <span className="font-medium text-[#1a1a2e]">Ouvidoria Pendente</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </Link>

                  <Link
                    to="/admin/casos"
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                    data-testid="quick-todos"
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-[#0EA5E9]" />
                      <span className="font-medium text-[#1a1a2e]">Ver Todos os Casos</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
